import {
  parseJSONFromSelector,
  extractLocations,
  haversine,
  fetchInventory,
  isSameLocation,
  isWarehouseLocation,
  isCountryAllowed,
  readCCState,
  hasLocalPickup,
  getLocationPickup,
  isCcStore,
} from '@cc/utils';

if (!customElements.get('cc-availability-indicator')) {
  customElements.define(
    'cc-availability-indicator',
    class CCAvailabilityIndicator extends HTMLElement {
      constructor() {
        super();
        this.i18n = {};
        this.productId = null;
        this.selectedVariantId = null;
        this.selectedStore = null;
        this.selectedStoreGid = null;
        this.allStores = [];
        this.radiusKm = 0;
        this.warehouseGid = null;
        this.inventoryData = null;
        this._inventoryFetchPromise = null;
        this.abortController = null;
        this.onVariantChangeUnsubscriber = null;
        this._countryBlocked = false;
      }

      connectedCallback() {
        this.abortController = new AbortController();

        this.i18n = this.parseJSON('[data-cc-availability-indicator-i18n]') || {};
        this.productId = this.dataset.productId;

        const storeData = this.parseJSON('[data-cc-availability-indicator-stores]') || {};
        this.allStores = storeData.stores || [];
        this.radiusKm = Number(storeData.radius_km) || 0;
        this.warehouseGid = storeData.warehouse_gid || null;

        this.hydrateInitialState();
        this.bindEvents();

        // Kick off the inventory fetch immediately — in parallel with the
        // country-allowed check below, which is an independent network call.
        // By the time the country check resolves, the inventory data will
        // often already be cached, eliminating the visible render delay.
        this._fetchInventory().catch(() => {});

        // Only show indicator for allowed countries
        this.checkCountryAndInit();
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber?.();
        this._fetchAbort?.abort();
        this.abortController?.abort();
      }

      parseJSON(selector) {
        return parseJSONFromSelector(this, selector);
      }

      async checkCountryAndInit() {
        const selectorEl = document.querySelector('cc-store-selector [data-cc-store-selector-data]');
        if (selectorEl) {
          try {
            const selectorData = JSON.parse(selectorEl.textContent);
            const allowedCountries = (selectorData?.settings?.allowed_countries || '')
              .split(',')
              .map((c) => c.trim().toUpperCase())
              .filter(Boolean);

            const allowed = await isCountryAllowed(allowedCountries, this.abortController.signal);
            if (!allowed) {
              this._countryBlocked = true;
              this.hideIndicator();
              return;
            }
          } catch {
            // Network error or aborted — proceed with rendering
          }
        }

        this.updateIndicator();
        this.checkAllVariants();
      }

      bindEvents() {
        const signal = this.abortController.signal;

        // Listen for variant changes
        this.onVariantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          const variant = event.data?.variant;
          if (variant) {
            this.selectedVariantId = variant.id;
            // Proxy response contains all variants for the product, so the
            // cached inventoryData is still valid — no need to refetch.
            this.updateIndicator();
          } else {
            this.selectedVariantId = null;
            this.hideIndicator();
          }
          // Re-check after DOM morph — new inputs have updated data-variant-id
          this.checkAllVariants();
        });

        // Listen for store selector changes
        window.addEventListener(
          'zippy:cc-store-selector:change',
          (event) => {
            const { store, storeGid } = event.detail;
            this.selectedStore = store;
            this.selectedStoreGid = storeGid;
            // Inventory data (per-location stock) does not change with the
            // store selection — only the interpretation of allowed locations.
            // Keep cached inventoryData to avoid an extra /apps/proxy hit
            // (Cloudflare rate-limits bursts of parallel proxy requests).
            this.updateIndicator();
            this.checkAllVariants();
          },
          { signal },
        );
      }

      hydrateInitialState() {
        this.hydrateSelectedStore();
        this.hydrateSelectedVariant();
      }

      hydrateSelectedStore() {
        const parsed = readCCState();
        if (!parsed) {
          this.selectedStore = null;
          this.selectedStoreGid = null;
          return;
        }

        const selectedStoreId = parsed?.selectedStoreId;
        this.selectedStoreGid = parsed?.selectedStoreGid || null;
        if (!selectedStoreId) return;

        this.selectedStore =
          this.allStores.find((store) => String(store.location_id) === String(selectedStoreId)) || null;
      }

      hydrateSelectedVariant() {
        const productInfo = this.closest('product-info');
        if (!productInfo) return;

        const variantSelects = productInfo.querySelector('variant-selects');
        if (!variantSelects) {
          // Products with no real variant options never render
          // <variant-selects> — treat the single default variant as
          // already selected so the indicator doesn't wait forever.
          if (this.dataset.hasOnlyDefaultVariant === 'true') {
            this.selectedVariantId = Number(this.dataset.defaultVariantId) || null;
          }
          return;
        }

        // Check if all option fieldsets have a checked input (i.e. variant is truly selected)
        const totalOptions = variantSelects.querySelectorAll('fieldset').length;
        const selectedOptions = variantSelects.querySelectorAll('fieldset:has(input:checked)').length;

        if (totalOptions > 0 && selectedOptions < totalOptions) {
          this.selectedVariantId = null;
          return;
        }

        const selectedVariantRaw = variantSelects
          .querySelector('[data-selected-variant]')
          ?.textContent?.trim();

        if (!selectedVariantRaw) return;

        try {
          const variant = JSON.parse(selectedVariantRaw);
          this.selectedVariantId = variant?.id || null;
        } catch {
          this.selectedVariantId = null;
        }
      }

      async updateIndicator() {
        if (this._countryBlocked) return;

        // Store selected, but Click & Collect is disabled for it in Shopify
        // settings — never prompt for a size or show stock-based messaging
        // for this store; the customer can still buy in-store or online, just
        // not via Click & Collect pickup here.
        if (this.selectedStore && !this._isStoreActive(this.selectedStore)) {
          this.toggleAddToCart(await this._shouldDisableAddToCart());
          this.renderScenario({
            scenario: 'disabled',
            data: { storeName: this.selectedStore.name || '' },
          });
          return;
        }

        if (!this.selectedStore) {
          // No store selected — still check inventory if a variant is picked
          // to block purchase of store-exclusive items that have no warehouse
          // stock (they cannot be fulfilled online).
          this.toggleAddToCart(await this._shouldDisableAddToCart());
          this.renderScenario({ scenario: 'no-store', data: {} });
          return;
        }

        if (!this.selectedVariantId) {
          // No size yet, but pickup is a location setting — a store without
          // it must not be offered for Click & Collect before a size is picked.
          const pickup = await this._selectedStorePickup();
          if (pickup === undefined) return;
          this.renderScenario({
            scenario: pickup === false ? 'disabled' : 'default',
            data: { storeName: this.selectedStore.name || '' },
          });
          return;
        }

        // Abort any in-flight inventory fetch before starting a new one
        this._fetchAbort?.abort();
        this._fetchAbort = new AbortController();

        try {
          if (!this.inventoryData) {
            this.inventoryData = await this._fetchInventory(this._fetchAbort.signal);
          }

          if (!this.inventoryData) {
            this.hideIndicator();
            return;
          }

          const scenario = this.determineScenario();
          this.renderScenario(scenario);
          this.toggleAddToCart(scenario.scenario === 'out-of-radius' || Boolean(scenario.disableAddToCart));
        } catch (err) {
          console.error('[cc-availability-indicator]', err);
          this.hideIndicator();
        }
      }

      async _fetchInventory(signal) {
        if (this.inventoryData) return this.inventoryData;
        if (!this._inventoryFetchPromise) {
          this._inventoryFetchPromise = fetchInventory(
            this.productId,
            signal || this.abortController.signal,
          ).catch((err) => {
            this._inventoryFetchPromise = null;
            throw err;
          });
        }
        return this._inventoryFetchPromise;
      }

      /**
       * Local pickup setting of the selected store's location, fetching the
       * product's inventory if needed.
       * @returns {Promise<boolean|null|undefined>} true/false, null when
       *   unknown (fetch failed, store not stocked, older proxy), or undefined
       *   when a newer update aborted this one and owns the render.
       */
      async _selectedStorePickup() {
        // Claim the render slot like the other update paths, but don't pass
        // its signal to the fetch: _fetchInventory() shares one promise, and
        // an aborted first caller would resolve it to null for every later one.
        this._fetchAbort?.abort();
        const fetchAbort = new AbortController();
        this._fetchAbort = fetchAbort;
        if (!this.inventoryData) {
          const data = await this._fetchInventory().catch(() => null);
          if (this._fetchAbort !== fetchAbort) return undefined;
          this.inventoryData = data;
        }
        return getLocationPickup(this.inventoryData, (loc) => this._isSameLocation(loc));
      }

      /**
       * Whether add-to-cart should be disabled because the selected variant
       * has no warehouse stock. Used when there is effectively no usable
       * store context (no store selected, or the selected store has Click &
       * Collect disabled) — only online/warehouse fulfillment applies.
       */
      async _shouldDisableAddToCart() {
        if (!this.selectedVariantId) return false;

        this._fetchAbort?.abort();
        this._fetchAbort = new AbortController();
        try {
          if (!this.inventoryData) {
            this.inventoryData = await this._fetchInventory(this._fetchAbort.signal);
          }
          if (!this.inventoryData) return false;

          const locations = extractLocations(this.inventoryData, this.selectedVariantId) || [];
          const warehouseStock = locations
            .filter((loc) => this._isWarehouse(loc))
            .reduce((sum, loc) => sum + (loc.available || 0), 0);

          return warehouseStock === 0;
        } catch {
          // Aborted or network error — don't block purchase on a transient failure
          return false;
        }
      }

      determineScenario() {
        if (!this.inventoryData || !this.inventoryData.data) {
          return { scenario: 'no-stock', data: {} };
        }

        const locations = extractLocations(this.inventoryData, this.selectedVariantId);
        if (!locations || locations.length === 0) {
          return { scenario: 'no-stock', data: {} };
        }

        const selectedStoreLocation = locations.find((loc) => this._isSameLocation(loc));
        // Only stores configured as Click & Collect-active are valid fulfillment
        // options here — a store with stock that isn't C&C-active must never be
        // suggested to the customer.
        const storeLocations = locations.filter((loc) => !this._isWarehouse(loc) && this._isCcActive(loc));
        const warehouseLocations = locations.filter((loc) => this._isWarehouse(loc));

        const selectedStoreStock = selectedStoreLocation?.available || 0;
        const nearbyStores = this.getNearbyStores(locations);
        const warehouseStock = warehouseLocations.reduce((sum, loc) => sum + (loc.available || 0), 0);

        // The selected store's location has local pickup off: same as a store
        // with Click & Collect disabled — no pickup messaging, and the size
        // can only be bought online.
        if (getLocationPickup(this.inventoryData, (loc) => this._isSameLocation(loc)) === false) {
          return {
            scenario: 'disabled',
            data: { storeName: this.selectedStore.name || '' },
            disableAddToCart: warehouseStock === 0,
          };
        }

        if (selectedStoreStock > 0) {
          const otherStoresWithStock = storeLocations.filter(
            (loc) => !this._isSameLocation(loc) && loc.available > 0,
          );

          if (otherStoresWithStock.length === 0 && warehouseStock === 0) {
            return {
              scenario: 'exclusive',
              data: { storeName: this.selectedStore.name },
            };
          } else {
            return {
              scenario: 'available',
              data: { storeName: this.selectedStore.name },
            };
          }
        }

        const nearbyWithStock = nearbyStores.filter((loc) => loc.available > 0);
        if (nearbyWithStock.length > 0) {
          const nearbyNames = nearbyWithStock.slice(0, 2).map((loc) => loc.name);
          const hasMore = nearbyWithStock.length > 2;

          return {
            scenario: 'nearby',
            data: {
              selectedStoreName: this.selectedStore.name,
              nearbyStores: nearbyNames,
              hasMore,
            },
          };
        }

        if (warehouseStock > 0) {
          return {
            scenario: 'online-only',
            data: { selectedStoreName: this.selectedStore.name },
          };
        }

        // Stock exists in stores outside radius but not online
        const allStoreStock = storeLocations.reduce((sum, loc) => sum + (loc.available || 0), 0);
        if (allStoreStock > 0 && this.radiusKm > 0) {
          return {
            scenario: 'out-of-radius',
            data: { radiusKm: this.radiusKm },
          };
        }

        return { scenario: 'no-stock', data: {} };
      }

      getNearbyStores(locations) {
        const selectedLat = Number(this.selectedStore?.latitude);
        const selectedLng = Number(this.selectedStore?.longitude);
        if (!Number.isFinite(selectedLat) || !Number.isFinite(selectedLng)) return [];

        const storeLocations = locations.filter((loc) => !this._isWarehouse(loc) && this._isCcActive(loc));
        const nearby = [];

        for (const loc of storeLocations) {
          if (this._isSameLocation(loc)) continue;
          if (!loc.lat || !loc.lng) continue;

          const distance = haversine(selectedLat, selectedLng, loc.lat, loc.lng);
          if (this.radiusKm > 0 && distance > this.radiusKm) continue;
          nearby.push({ ...loc, distance });
        }

        return nearby.sort((a, b) => a.distance - b.distance);
      }

      _isSameLocation(location) {
        return isSameLocation(location, this.selectedStore?.location_id, this.selectedStoreGid);
      }

      _isWarehouse(loc) {
        return isWarehouseLocation(loc, this.warehouseGid);
      }

      /**
       * True when the location matches an active store in this market's
       * Click & Collect list and its Shopify location has local pickup on.
       */
      _isCcActive(loc) {
        const meta = this.allStores?.find((s) => s.location_id === loc?.id);
        return Boolean(meta) && this._isStoreActive(meta) && hasLocalPickup(loc);
      }

      // Store entry has Click & Collect on: status on and local pickup on.
      _isStoreActive(store) {
        return isCcStore(store);
      }

      renderScenario(scenario) {
        const container = this.querySelector('[ref="indicatorContent"]');
        if (!container) return;

        const { scenario: type, data } = scenario;

        if (type === 'no-stock') {
          this.hideIndicator();
          return;
        }

        const template = this.querySelector(`template[data-scenario="${type}"]`);
        if (!template) {
          this.hideIndicator();
          return;
        }

        const fragment = template.content.cloneNode(true);

        const storeNameSlot = fragment.querySelector('[data-slot="store-name"]');
        if (storeNameSlot) storeNameSlot.textContent = data.storeName ?? '';

        const selectedStoreSlot = fragment.querySelector('[data-slot="selected-store-name"]');
        if (selectedStoreSlot) selectedStoreSlot.textContent = data.selectedStoreName ?? '';

        const nearbySlot = fragment.querySelector('[data-slot="nearby-list"]');
        if (nearbySlot && data.nearbyStores) {
          const nearbyText = data.nearbyStores.join(', ');
          const moreText = data.hasMore ? `<span class="font-normal"> ${this.i18n.and_more}</span>` : '';
          nearbySlot.innerHTML = nearbyText + moreText;
        }

        const outOfRadiusSlot = fragment.querySelector('[data-slot="out-of-radius-message"]');
        if (outOfRadiusSlot && data.radiusKm != null) {
          outOfRadiusSlot.textContent = (this.i18n.out_of_radius || '').replace('{radius}', data.radiusKm);
        }

        container.innerHTML = '';
        container.appendChild(fragment);
        this.classList.remove('hidden');

        const storeLink = container.querySelector('[data-open-store-selector]');
        if (storeLink) {
          storeLink.addEventListener(
            'click',
            (e) => {
              e.preventDefault();
              document.querySelector('[data-cc-store-selector-open]')?.click();
            },
            { signal: this.abortController?.signal },
          );
        }
      }

      hideIndicator() {
        this.classList.add('hidden');
        this.toggleAddToCart(false);
      }

      toggleAddToCart(disable) {
        const section = this.closest('.shopify-section') || this.closest('section');
        if (!section) return;
        section.querySelectorAll('product-form button[type="submit"][name="add"]').forEach((btn) => {
          btn.disabled = disable;
          btn.classList.toggle('cc-add-to-cart-disabled', disable);
        });
      }

      /**
       * Check variant availability for ALL variant buttons on PDP.
       *
       * - No store selected: disable variants without warehouse stock
       *   (cannot be purchased online).
       * - Store selected: disable variants whose only stock is at stores
       *   outside the configured radius (selected store + nearby) with no
       *   warehouse fallback.
       *
       * Single request — proxy returns all variants for the product.
       */
      async checkAllVariants() {
        const productInfo = this.closest('product-info');
        if (!productInfo) return;

        const inputs = productInfo.querySelectorAll('variant-selects input[data-variant-id]');
        if (!inputs.length) return;

        const data = await fetchInventory(this.productId);
        if (!data) return;

        const selectedLat = Number(this.selectedStore?.latitude);
        const selectedLng = Number(this.selectedStore?.longitude);
        const hasSelectedCoords =
          this.selectedStore && Number.isFinite(selectedLat) && Number.isFinite(selectedLng);
        const selectedHasPickup =
          getLocationPickup(data, (loc) => this._isSameLocation(loc)) !== false;

        for (const input of inputs) {
          // Skip natively disabled variants (truly out of stock)
          if (input.disabled && !input.classList.contains('cc-variant-unavailable')) continue;

          const variantId = input.dataset.variantId;
          if (!variantId) continue;

          const locations = extractLocations(data, variantId) || [];
          const hasWarehouseStock = locations.some(
            (loc) => this._isWarehouse(loc) && loc.available > 0,
          );

          let available;
          if (!this.selectedStore || !this._isStoreActive(this.selectedStore) || !selectedHasPickup) {
            // No store, or Click & Collect disabled for the selected store
            // (status off, or local pickup off on its location) → variant
            // must have warehouse stock to be purchasable online.
            available = hasWarehouseStock;
          } else if (hasWarehouseStock) {
            available = true;
          } else {
            // No warehouse stock → variant must have stock at the selected
            // store or any in-radius nearby store.
            const inRadius = locations.some((loc) => {
              if (this._isWarehouse(loc) || (loc.available || 0) <= 0) return false;
              if (this._isSameLocation(loc)) return true;
              if (!this._isCcActive(loc)) return false;
              if (!hasSelectedCoords || !loc.lat || !loc.lng) return false;
              if (this.radiusKm <= 0) return true;
              return (
                haversine(selectedLat, selectedLng, loc.lat, loc.lng) <= this.radiusKm
              );
            });
            available = inRadius;
          }

          if (!available) {
            this._disableVariantInput(input);
          } else if (input.classList.contains('cc-variant-unavailable')) {
            this._enableVariantInput(input);
          }
        }
      }

      _disableVariantInput(input) {
        input.disabled = true;
        input.classList.add('cc-variant-unavailable');
        const label = input.closest('label') || document.querySelector(`label[for="${input.id}"]`);
        if (label) {
          label.classList.add('cc-product-variant', '!font-normal', '!text-gray-500', '!cursor-not-allowed', 'md:!text-gray-400', 'md:!border-gray-300');
        }
      }

      _enableVariantInput(input) {
        input.disabled = false;
        input.classList.remove('cc-variant-unavailable');
        const label = input.closest('label') || document.querySelector(`label[for="${input.id}"]`);
        if (label) {
          label.classList.remove('cc-product-variant','!font-normal', '!text-gray-500', '!cursor-not-allowed', 'md:!text-gray-400', 'md:!border-gray-300');
        }
      }
    },
  );
}
